import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { isNotNull } from '@/utils/helpers';
import { RootState } from '../../store';
import { postSharedByMeV2Search, CurrentUserQuizzesV2SearchRequestBody } from '@/features/Quiz/api/getQuizzes';
import { QuizSharedByAdminV2Row } from '@/features/Quiz/types';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';

const PAGE_SIZE = 20;

const selectSharedRowId = (row: QuizSharedByAdminV2Row) => `${row.quizId}-${row.sharedWith}-${row.sharedAt ?? '0'}`;

export const getAllQuizzesSharedByMeV2 = createAsyncThunk<
    { items: QuizSharedByAdminV2Row[]; page: number; total: number; totalPages: number },
    {
        activePage: number;
        activeSearchKeyword?: string;
    },
    { rejectValue: string }
>('quizzes/getAllQuizzesSharedByMeV2', async ({ activePage, activeSearchKeyword }) => {
    const body: CurrentUserQuizzesV2SearchRequestBody = {
        page: activePage - 1,
        size: PAGE_SIZE,
        sort: [['sharedAt', 'desc']],
        ...(activeSearchKeyword?.trim() ? { title: activeSearchKeyword.trim() } : {}),
    };
    const res = await postSharedByMeV2Search(body);
    const totalPages = res.size > 0 ? Math.max(1, Math.ceil(res.total / res.size)) : 1;

    return {
        items: res.data ?? [],
        page: activePage,
        total: res.total,
        totalPages,
    };
});

const quizSharedByMeV2Adapter = createEntityAdapter<QuizSharedByAdminV2Row>({
    selectId: selectSharedRowId,
});

type State = {
    pages: Record<number, string[]>;
    activePage: number;
    total: number;
    totalPages: number;
    isLoading: boolean;
};

const quizSharedByMeV2Slice = createSlice({
    name: 'quizSharedByMeV2',
    initialState: quizSharedByMeV2Adapter.getInitialState<State>({
        pages: {},
        activePage: 1,
        totalPages: 1,
        total: 0,
        isLoading: false,
    }),
    reducers: {
        setQuizSharedByMeV2ActivePage: (state, action: PayloadAction<number>) => {
            state.activePage = action.payload;
        },
        resetQuizSharedByMeV2Pages: (state) => {
            state.pages = {};
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getAllQuizzesSharedByMeV2.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(getAllQuizzesSharedByMeV2.fulfilled, (state, action) => {
            quizSharedByMeV2Adapter.upsertMany(state, action.payload.items);
            state.pages[action.payload.page] = action.payload.items.map(selectSharedRowId);
            state.total = action.payload.total;
            state.activePage = action.payload.page;
            state.totalPages = action.payload.totalPages;
            state.isLoading = false;
        });
        builder.addCase(getAllQuizzesSharedByMeV2.rejected, (state) => {
            state.isLoading = false;
        });
    },
});

export const quizSharedByMeV2 = quizSharedByMeV2Slice.reducer;
export const { resetQuizSharedByMeV2Pages, setQuizSharedByMeV2ActivePage } = quizSharedByMeV2Slice.actions;

const selectState = (state: RootState) => state.quizzes.quizSharedByMeV2;

export const { selectById: selectQuizSharedByMeV2ById } = quizSharedByMeV2Adapter.getSelectors(selectState);

const selectRootState = (state: RootState) => state;

const selectIdsByPage = createSelector(
    [selectState, (_: RootState, page: number) => page],
    (state, page) => state.pages[page] ?? []
);

export const selectQuizSharedByMeV2Page = createSelector(
    [selectRootState, (state: RootState, page: number) => selectIdsByPage(state, page)],
    (state, ids) => ids.map((id) => selectQuizSharedByMeV2ById(state, id)).filter(isNotNull)
);

export const selectQuizSharedByMeV2ActivePage = createSelector([selectState], (state) => state.activePage);
export const selectQuizSharedByMeV2TotalPages = createSelector([selectState], (state) => state.totalPages);
export const selectQuizSharedByMeV2Loading = createSelector([selectState], (state) => state.isLoading);
